import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { ParentContext } from "components/constants/ParentContext";
import { selectCurrentSubscription } from "components/subscription/subscriptionSlice";
import { mixpanelCustomEvent } from "components/mixpanel/eventTriggers";
import { MixpanelEventName } from "components/mixpanel/types";
import { resetCampaignState } from "components/campaign/campaignSlice";
import { SUBSCRIPTION_STATUS } from "components/subscription/types";
import { FEATURE_HIGHLIGHT_NAME } from "components/campaign/types";
import { getCommunityAccessToken } from "./service";
import { getNotificationsCount } from "./notifications/service";
import { setNotificationCount } from "./notifications/notificationSlice";
import { getUnreadMessageCount } from "../../components/messaging-thread/service";
import {
  setSelectedThreadId,
  setUnreadMessageCount,
} from "../../components/messaging-thread/messagingThreadSlice";

export const useSidebar = (user: any, loggedIn: boolean) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const context = useContext<any>(ParentContext);

  const [collapsed, setCollapsed] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [communityInvitationState, setCommunityInvitationState] = useState({
    showModal: false,
    message: "",
  });

  const currentSubscription = useSelector(selectCurrentSubscription);

  const theme =
    context.isHarbour || user?.isHarbourClub
      ? "harbour"
      : context.isBiz4Biz || user?.isBiz4Biz
      ? "biz4biz"
      : "bizcrunch";

  useEffect(() => {
    const pathToKeyMap: Record<string, string> = {
      "/": "search",
      "/collections": "collections",
      "/recentSearches": "recentSearches",
      "/campaign": "campaign",
      "/deals": "deal",
      "/billing": "billing",
      "/admin": "admin",
      "/community": "community",
      "/threads": "threads",
    };

    const key = pathToKeyMap[location.pathname];
    if (key) setSelected(key);
  }, [location]);

  const mixpanelProps = useMemo(
    () => ({
      $name: `${user?.name}`,
      $email: user?.email,
    }),
    [user?.name, user?.email]
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      const unreadNotificationCount = await getNotificationsCount();
      const unreadMessageCount = await getUnreadMessageCount();
      dispatch(setNotificationCount(unreadNotificationCount.count));
      dispatch(setUnreadMessageCount(unreadMessageCount.count));
    };
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMixpanelEvent = useCallback(
    (tabName: string) => {
      mixpanelCustomEvent({
        mixpanelProps: {
          ...mixpanelProps,
          tabName: tabName,
        },
        id: user?.uid?.toString(),
        eventName: MixpanelEventName.userClickedSideBarMenuItem,
      });
    },
    [user?.uid, mixpanelProps]
  );

  const handleNavigation = useCallback(
    async (tabName: string, path: string, resetState: boolean = false) => {
      handleMixpanelEvent(tabName);

      if (resetState) {
        dispatch(resetCampaignState());
        dispatch(setSelectedThreadId(""));
      }

      if (tabName === "Community") {
        try {
          const getAccessToken = await getCommunityAccessToken();

          if (getAccessToken?.data?.accessToken) {
            const communityAppUrl = process.env.REACT_APP_COMMUNITY_APP_URL;
            window.open(
              `${communityAppUrl}/session/cookies?access_token=${getAccessToken.data.accessToken}`,
              "_blank"
            );
          }
        } catch (error: any) {
          console.error("Failed to access Community:", error);
          if (error?.response?.data?.statusCode === 400) {
            setCommunityInvitationState({
              showModal: true,
              message: error?.response?.data?.message,
            });
          }
        }
      } else {
        navigate(path, { replace: true });
      }
      setSelected(tabName);
    },
    [handleMixpanelEvent, navigate, dispatch]
  );

  const navItems = [
    {
      label: "Search",
      key: "search",
      path: "/",
      icon: "Search",
      visible: true,
    },
    {
      label: "Recent Searches",
      key: "recentSearches",
      path: "/recentSearches",
      icon: "History",
      visible: loggedIn,
    },
    {
      label: "Collections",
      key: "collections",
      path: "/collections",
      icon: "ZoomIn",
      visible: loggedIn,
    },
    {
      label: "Campaigns",
      key: "campaign",
      path: "/campaign",
      icon: "Send",
      visible: loggedIn,
      resetState: true,
    },
    {
      label: "Deal Tracker",
      key: "deal",
      path: "/deals",
      icon: "SvgDealTracker",
      visible: loggedIn,
    },
    {
      label: "Community",
      key: "community",
      path: "/community",
      icon: "UsersRound",
      visible: loggedIn,
    },
    {
      label: "Admin",
      key: "admin",
      path: "/admin",
      icon: "ShieldCheck",
      visible: loggedIn && user?.isAdmin,
    },
  ];

  const isDisabled =
    currentSubscription?.status === SUBSCRIPTION_STATUS.CANCELLED;

  const renderProgressBars = () => {
    const quotas = currentSubscription?.userFeatureQuotas || [];
    if (!quotas.length) return null;

    return quotas.map((quota) => {
      const progress =
        quota?.usedQuota && quota?.totalQuota
          ? (quota.usedQuota / quota.totalQuota) * 100
          : 0;
      return { ...quota, progress };
    });
  };

  const renderCustomFeatureName = (featureName: string) => {
    switch (featureName) {
      case FEATURE_HIGHLIGHT_NAME.EMAIL_OUTREACH:
        return currentSubscription?.status === SUBSCRIPTION_STATUS.TRAILING
          ? "Emails Sent (Trial)"
          : "Emails Sent";
      case FEATURE_HIGHLIGHT_NAME.LETTER_OUTREACH:
        return currentSubscription?.status === SUBSCRIPTION_STATUS.TRAILING
          ? "Letters Sent (Trial)"
          : "Letters Sent";
      case FEATURE_HIGHLIGHT_NAME.COMPANY_COLLECTIONS:
        return currentSubscription?.status === SUBSCRIPTION_STATUS.TRAILING
          ? "Companies Collected (Trial)"
          : "Companies Collected";
      default:
        return "";
    }
  };

  const handleJoinCommunity = () => {
    const joinCommunityUrl = process.env.REACT_APP_COMMUNITY_REQUEST_INVITE_URL;

    if (joinCommunityUrl && user?.email) {
      window.open(
        `${joinCommunityUrl}${encodeURIComponent(user.email)}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
    setCommunityInvitationState({
      showModal: false,
      message: "",
    });
  };

  return {
    collapsed,
    setCollapsed,
    selected,
    setSelected,
    navItems,
    handleNavigation,
    isDisabled,
    renderProgressBars,
    theme,
    currentSubscription,
    renderCustomFeatureName,
    communityInvitationState,
    setCommunityInvitationState,
    handleJoinCommunity,
  };
};
